import React from 'react';
import {inject, IReactComponent, observer} from 'mobx-react';
import {CollapsiblePanelAnimated, ExpandState} from '@techsee/techsee-ui-common/lib/collapsible-panel-animated';
import {GalleryController, Image} from './GalleryController';
import emptyGalleryPlaceholderImage from '../../../../../../../img/empty-gallery-mini.png';
import loader from '../../../../../../../img/dots-spinner-white.gif';

import './GalleryMini.scss';
import failedImagePlaceholder from '../../../../../../../img/failed-image-placeholder.png';

interface GalleryMiniProps {
    isMobileView?: boolean;
}

interface GalleryMiniStoreProps extends GalleryMiniProps {
    controller: GalleryController;
    translate: (key: string) => string;
}

const Loader: React.FC<{id: string}> = ({id}) => (
    <div key={id} className='gallery-image-mini-spinner'>
        <img className={'img'} src={loader} alt='Loading' />
    </div>
);

const mapImages = (image: Image) => {
    if (image.isLoading) {
        return (
            <div key={image.id} className='gallery-carousel-item'>
                <Loader id={image.id} />
            </div>
        );
    }
    if (image.isFailed) {
        return (
            <div key={image.id} className='gallery-carousel-item failed-image-mini'>
                <img src={failedImagePlaceholder} alt='Failed image' />
            </div>
        );
    }

    return (
        <div key={image.id} className='gallery-carousel-item'>
            <img src={image.image} className='gallery-image-mini' alt='Image' />
        </div>
    );
};

const GalleryComponent: React.FC<GalleryMiniStoreProps> = observer(({translate, controller, isMobileView}) => {
    const title = <div className='header'>{translate('DASHBOARD_MINI.VIEW.GALLERY')}</div>;

    const emptyGalleryPlaceholder = (
        <div className='empty-gallery-mini-wrapper'>
            <img src={emptyGalleryPlaceholderImage} alt='No images yet' />
            <div className='empty-gallery-mini-text'>{translate('MAIN.VIEW.NO_IMAGES_YET')}</div>
        </div>
    );

    const images = controller.images.map(mapImages).reverse();

    // Use horizontal scrolling carousel for mobile, vertical list for desktop
    const galleryContent =
        controller.images.length > 0 ? (
            isMobileView ? (
                <div className='gallery-horizontal-carousel'>{images}</div>
            ) : (
                <>{images}</>
            )
        ) : (
            emptyGalleryPlaceholder
        );

    return (
        <CollapsiblePanelAnimated
            className='gallery-images-list-mini'
            title={title}
            expandState={ExpandState.ALWAYS_EXPANDED}
            unstyledHeader>
            {galleryContent}
        </CollapsiblePanelAnimated>
    );
});

const mapStoresToProps = (stores: any, ownProps: GalleryMiniProps): GalleryMiniStoreProps => ({
    translate: stores.multipartyDashboardStore.localizationService.translate,
    controller: stores.multipartyDashboardStore.galleryController,
    isMobileView: ownProps.isMobileView
});

export const GalleryMini = inject(mapStoresToProps)(GalleryComponent) as IReactComponent;
